<template>
	<ValidationObserver v-slot="{ passes, invalid }" ref="validateObserver" class="account-form" tag="form" @submit.prevent>
		<div class="form-group">
			<TextInput
				v-model="teamData.name"
				:disabled="loading"
				:label="$t('labels.teamName', { character: '*' })"
				:placeholder="$t('labels.teamNamePlaceholder')"
				name="team name"
				rules="required|max:128"
			/>
		</div>

		<div class="form-group">
			<OptionsSelect
				v-model="teamData.sportType"
				:disabled="loading"
				:label="$t('labels.sport')"
				:options="sportTypes"
				:placeholder="sportTypes[0] && sportTypes[0].title || $t('labels.sportPlaceholder')"
				key-value="name"
				name="sport"
				rules="required"
			/>
		</div>

		<div class="form-group">
			<OptionsSelect
				v-if="isSupportedGenders"
				v-model="teamData.gender"
				:disabled="loading"
				:label="$t('labels.gender', { character: '*' })"
				:options="gender"
				:placeholder="$t('labels.genderPlaceholder')"
				name="gender"
				rules="required"
			/>
		</div>

		<div class="form-group">
			<OptionsSelect
				v-if="isSupportedAmericanFootballTypes"
				v-model="teamData.teamType"
				:disabled="loading"
				:label="$t('labels.teamType', { character: '*' })"
				:options="americanFootballTypes"
				:placeholder="$t('labels.teamType', { character: '' })"
				name="Team Type"
				rules="required"
			/>
		</div>

		<div class="mt-75 text-center">
			<button v-loading="loading" :disabled="loading || invalid" class="button-light-blue" @click="passes(save)">
				{{ $tc('actions.save') }}
			</button>
		</div>
	</ValidationObserver>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { TextInput, OptionsSelect } from '~/components/interface'
import { supportedSportTypeGenders, supportedTeamTypes } from '~/assets/util/helpers/filters'

export default {
	name: 'AddTeamForm',
	components: {
		OptionsSelect,
		ValidationObserver,
		TextInput
	},
	props: {
		teams: {
			type: Array,
			required: false,
			default: () => []
		},
		autoSave: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			loading: false,
			teamData: {
				name: '',
				sportType: '',
				gender: '',
				teamType: ''
			}
		}
	},
	computed: {
		...mapGetters({
			sportTypesStore: 'user/sportTypes',
			cameraFlow: 'plans/cameraFlow'
		}),

		sportTypes () {
			return this.sportTypesStore.filter(sport => sport.cameraTypes.includes(this.cameraFlow) && !sport.disabled)
		},

		sportTypesMap () {
			return this.sportTypesStore.reduce((acc, item) => {
				acc[item.name] = item.title
				return acc
			}, {})
		},
		gender () {
			return [
				{ id: 'boys', title: this.$tc('plans.wizard.signUpForm.boysText') },
				{ id: 'girls', title: this.$tc('plans.wizard.signUpForm.girlsText') }
			]
		},
		americanFootballTypes () {
			return [
				{ id: 'outdoor-football', title: this.$tc('plans.wizard.signUpForm.outdoorFootball') },
				{ id: 'flag-football', title: this.$tc('plans.wizard.signUpForm.indoorFootball') },
				{ id: 'canadian-football', title: this.$tc('plans.wizard.signUpForm.canadianFootball') }
			]
		},
		isSupportedGenders () {
			return supportedSportTypeGenders(this.teamData.sportType)
		},
		isSupportedAmericanFootballTypes () {
			return supportedTeamTypes(this.teamData.sportType)
		}
	},
	watch: {
		autoSave (newValue) {
			if (newValue) {
				this.$refs.validateObserver.validate()
					.then((isValid) => {
						if (isValid) {
							this.save()
						} else {
							this.$emit('error')
						}
					})
			}
		}
	},
	methods: {
		...mapActions({ addTeamAction: 'teams/createTeam' }),

		async save () {
			this.loading = true

			try {
				await this.addTeamAction(this.teamData)

				const sport = this.sportTypesMap[this.teamData.sportType].toLowerCase()

				this.$toast.success(`${this.$tc('successMessages.addTeamSuccessMessage[0]')} ${sport} ${this.$tc('successMessages.addTeamSuccessMessage[1]')}`)
			} catch (error) {
				this.$toast.error(this.$tc('errors.createNewTeamError'))
				this.$log.error('Failed add new team data', error)
			}

			this.loading = false
			this.$emit('success')
		}
	}
}
</script>
